// The atmosphere as the server states it (simulation/src/atmosphere.py, solved on the GPU by atmos/radiance.cuh), read by an observer anywhere. No three.js here, so it can be run and
// checked on its own (the test builds it with esbuild and holds it to the solver's own reader kernel).
//
// What the message holds, and what this does with it:
//   trans  the star's beam through the air from (r, mu) to the top, as a colour relative to the beam outside it      -> the beam at the eye, and on every step of a view ray
//   ms     everything scattered more than once and every ground bounce, as radiance per unit top-of-atmosphere irradiance, seen from (r, mu) with the star at (mu_s, nu)
//   esky   the diffuse irradiance on a horizontal surface at height r with the star at mu_s, per unit top-of-atmosphere irradiance
// Single scattering is not in the message: `single` marches the view ray through the air (the same spherical geometry and the same step law as the solver's kernel, with colours in place
// of bands), so the air a renderer has (the painters' haze) can stand in for the world's mean.
//
// An observer is a height above the planet (m), the star's cosine from the zenith at the eye (mu_s, the sine of its elevation) and a view direction (mu: its cosine from the zenith; nu: its
// cosine from the star). Nothing here knows a place on the planet; the tables are spherically symmetric and the observer's frame (render axes, y up) carries the rest.
// The observer may be anywhere: above the top of the air a view ray starts where it enters the air (`entry`), and a ray that never meets the air has no radiance and a transmittance of 1.
//
// UNITS. Radiance and irradiance are per unit star irradiance outside the atmosphere on a surface facing the star, as linear sRGB: `sunToa` is that irradiance as colour (luminance 1), so
// the star's absolute scale (`flux`) is the caller's. The old pipeline's sky is `skyRgb * shape`, with `shape` a mean of 1 over the cosine-weighted hemisphere: its dome value is pi times
// the radiance, because skyRgb is an irradiance. `DOME_PI` is that factor, in one place.
//
// The parametrisation is the solver's (radiance.cuh) and must follow it exactly:
//   r:    h = H u^2, u = i / (n - 1).
//   mu:   two halves split at the horizon cosine mu_h(r) = -sqrt(1 - (Rb / r)^2), never blended; the lower half looks at the ground and ends at mu_h, the upper half starts at mu_h.
//         Node t = k / (nMu / 2 - 1): mu = mu_h -+ t^2 (1 -+ mu_h).
//   mu_s: explicit ascending nodes (sin of the elevation), linear in mu_s, clamped.
//   nu:   uniform in [-1, 1], clamped to what (mu, mu_s) allow.
//   trans: the same r axis (t_r nodes), mu = mu_h + t^2 (1 - mu_h) with t uniform (t_mu nodes); only rays that do not meet the ground.

export const DOME_PI = Math.PI;

export interface AtmosphereMessage {
  type: 'atmosphere';
  version: number;
  planet_radius: number;
  top_radius: number;
  dims: { t_r: number; t_mu: number; r: number; mu: number; mu_s: number; nu: number };
  mu_s_nodes: number[];
  sun_toa_rgb: number[];
  rayleigh_scale: number;
  mie_scale: number;
  mie_g: number;
  ozone_du: number;
  ground_albedo: number;
  rgb: { rayleigh_scatter: number[]; rayleigh_ext: number[]; mie_scatter: number[]; mie_ext: number[]; ozone_ext: number[] };
  trans: string;
  ms: string;
  esky: string;
  /** The light a night leaves with no star up, on a horizontal surface (sun message units): the tables hold the star's light, not this. */
  airglow?: number;
}

export type V3 = [number, number, number];

/** The air a view ray goes through, relative to the world's mean. */
export interface Medium {
  /** Aerosol density relative to the mean (the painters' haze); 1 is the world's mean. */
  aerosol: number;
}
export const MEAN_MEDIUM: Medium = { aerosol: 1 };

const clamp = (x: number, a: number, b: number) => (x < a ? a : x > b ? b : x);

function b64f32(s: string): Float32Array {
  const bin = atob(s);
  const n = bin.length >> 2;
  const buf = new ArrayBuffer(n * 4);
  const u8 = new Uint8Array(buf);
  for (let i = 0; i < u8.length; i++) u8[i] = bin.charCodeAt(i);
  return new Float32Array(buf);       // (the message is little-endian float32; so is every platform this runs on)
}

const phaseR = (c: number) => (3 / (16 * Math.PI)) * (1 + c * c);
function phaseM(c: number, g: number): number {      // Cornette-Shanks
  const g2 = g * g, d = 1 + g2 - 2 * g * c;
  return (3 / (8 * Math.PI)) * (1 - g2) * (1 + c * c) / ((2 + g2) * d * Math.sqrt(d));
}
const rhoO = (h: number) => (h <= 10000 || h >= 40000 ? 0 : h < 25000 ? (h - 10000) / 15000 : (40000 - h) / 15000);

export class Atmosphere {
  readonly Rb: number; readonly Rt: number; readonly H: number;
  readonly nR: number; readonly nMu: number; readonly nS: number; readonly nN: number; readonly tR: number; readonly tMu: number;
  readonly nodes: Float64Array;
  readonly sunToa: V3;
  /** The night's floor (airglow), per channel, in the sun message's units. */
  readonly airglow: number;
  readonly hR: number; readonly hM: number; readonly g: number; readonly albedo: number;
  /** The tables as the message has them (read in place by the GPU upload: atmosphereGpu.ts). */
  readonly trans: Float32Array;       // (t_r, t_mu, 3)
  readonly ms: Float32Array;          // (r, mu, mu_s, nu, 3)
  readonly esky: Float32Array;        // (r, mu_s, 3)
  /** ln of the beam's transmittance, floored at 1e-12 (what is interpolated: the beam falls by orders of magnitude across a cell near the horizon). */
  readonly lnT: Float32Array;
  private readonly kRs: V3; private readonly kRe: V3; private readonly kMs: V3; private readonly kMe: V3; private readonly kOe: V3;

  constructor(m: AtmosphereMessage) {
    this.Rb = m.planet_radius; this.Rt = m.top_radius; this.H = this.Rt - this.Rb;
    const d = m.dims;
    this.nR = d.r; this.nMu = d.mu; this.nS = d.mu_s; this.nN = d.nu; this.tR = d.t_r; this.tMu = d.t_mu;
    this.nodes = Float64Array.from(m.mu_s_nodes);
    this.sunToa = [m.sun_toa_rgb[0]!, m.sun_toa_rgb[1]!, m.sun_toa_rgb[2]!];
    this.airglow = typeof m.airglow === 'number' && m.airglow >= 0 ? m.airglow : 1e-9;
    this.hR = m.rayleigh_scale; this.hM = m.mie_scale; this.g = m.mie_g; this.albedo = m.ground_albedo;
    this.trans = b64f32(m.trans); this.ms = b64f32(m.ms); this.esky = b64f32(m.esky);
    const v3 = (a: number[]): V3 => [a[0]!, a[1]!, a[2]!];
    this.kRs = v3(m.rgb.rayleigh_scatter); this.kRe = v3(m.rgb.rayleigh_ext); this.kMs = v3(m.rgb.mie_scatter); this.kMe = v3(m.rgb.mie_ext); this.kOe = v3(m.rgb.ozone_ext);
    if (this.trans.length !== this.tR * this.tMu * 3 || this.ms.length !== this.nR * this.nMu * this.nS * this.nN * 3 || this.esky.length !== this.nR * this.nS * 3 || this.nodes.length !== this.nS) {
      throw new Error('atmosphere: the tables do not match their dimensions');
    }
    if (this.nMu % 2 || this.nR < 4 || this.nS < 4 || this.nN < 3 || this.tR < 4 || this.tMu < 8) throw new Error('atmosphere: dimensions the reader cannot use');
    this.lnT = new Float32Array(this.trans.length);
    for (let i = 0; i < this.trans.length; i++) this.lnT[i] = Math.log(Math.max(this.trans[i]!, 1e-12));
  }

  /** The message as an atmosphere, or null if it is not one this reader understands (a different version). */
  static fromMessage(m: any): Atmosphere | null {
    if (!m || m.type !== 'atmosphere' || m.version !== 2 || !m.rgb || typeof m.trans !== 'string') return null;
    return new Atmosphere(m as AtmosphereMessage);
  }

  // ── geometry ──────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
  /** The cosine from the zenith of the horizon seen from radius r (negative: it dips below the horizontal as the eye climbs). */
  muH(r: number): number { const s = this.Rb / Math.max(r, this.Rb); const q = 1 - s * s; return -Math.sqrt(q > 0 ? q : 0); }
  /** Cosine from the zenith of the geometric horizon for an eye `h` metres up. */
  horizonMu(h: number): number { return this.muH(this.Rb + Math.max(0, h)); }
  private distTop(r: number, mu: number): number { const d = r * r * (mu * mu - 1) + this.Rt * this.Rt; return -r * mu + Math.sqrt(d > 0 ? d : 0); }
  private distGround(r: number, mu: number): number { const d = r * r * (mu * mu - 1) + this.Rb * this.Rb; return -r * mu - Math.sqrt(d > 0 ? d : 0); }

  /** The node interval along r for an axis of n nodes (h = H u^2). */
  private radial(r: number, n: number): { i: number; f: number; mh: number } {
    if (r < this.Rb) r = this.Rb;
    const q = clamp((r - this.Rb) / this.H, 0, 1);
    const pr = Math.sqrt(q) * (n - 1);
    let i = Math.floor(pr); if (i > n - 2) i = n - 2;
    return { i, f: pr - i, mh: this.muH(r) };
  }

  /** The interval on the mu_s node list, the fraction, and the clamped value. */
  musIndex(mus: number): { c: number; f: number; mc: number } {
    const N = this.nodes, mc = clamp(mus, N[0]!, N[this.nS - 1]!);
    let c = 0;
    while (c < this.nS - 2 && mc >= N[c + 1]!) c++;
    return { c, f: (mc - N[c]!) / (N[c + 1]! - N[c]!), mc };
  }

  // ── the beam ──────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
  /** The star's beam through the air from the height `h` along a ray whose cosine from the zenith is `mu`, to the top: a colour relative to the beam outside (0 if the ray meets the ground). */
  trans3(h: number, mu: number, out: number[] | Float64Array = [0, 0, 0] as number[]): number[] | Float64Array {
    const rx = this.radial(this.Rb + Math.max(0, h), this.tR);
    if (mu < rx.mh) { out[0] = 0; out[1] = 0; out[2] = 0; return out; }
    const t = Math.sqrt(clamp((mu - rx.mh) / (1 - rx.mh), 0, 1)) * (this.tMu - 1);
    let j = Math.floor(t); if (j > this.tMu - 2) j = this.tMu - 2;
    const fj = t - j, fi = rx.f, T = this.lnT, tm = this.tMu;
    const a = ((rx.i * tm + j) * 3), b = a + 3, c = a + tm * 3, d = c + 3;
    for (let k = 0; k < 3; k++) {
      const v = (T[a + k]! * (1 - fj) + T[b + k]! * fj) * (1 - fi) + (T[c + k]! * (1 - fj) + T[d + k]! * fj) * fi;
      out[k] = v < -27.6 ? 0 : Math.exp(v);
    }
    return out;
  }

  /** The diffuse irradiance on a horizontal surface at height h with the star at mu_s (per unit star irradiance outside; a colour). */
  esky3(h: number, mus: number, out: number[] | Float64Array = [0, 0, 0] as number[]): number[] | Float64Array {
    const rx = this.radial(this.Rb + Math.max(0, h), this.nR), mx = this.musIndex(mus), E = this.esky, nS = this.nS;
    const a = (rx.i * nS + mx.c) * 3, b = a + 3, c = a + nS * 3, d = c + 3;
    for (let k = 0; k < 3; k++) out[k] = (E[a + k]! * (1 - mx.f) + E[b + k]! * mx.f) * (1 - rx.f) + (E[c + k]! * (1 - mx.f) + E[d + k]! * mx.f) * rx.f;
    return out;
  }

  // ── multiple scattering ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
  /** Everything scattered more than once (and every ground bounce), seen at height h looking along (mu, nu) with the star at mu_s; per unit star irradiance outside; a colour. */
  ms3(h: number, mu: number, mus: number, nu: number, out: number[] | Float64Array = [0, 0, 0] as number[]): number[] | Float64Array {
    const rx = this.radial(this.Rb + Math.max(0, h), this.nR), mx = this.musIndex(mus);
    const hf = this.nMu >> 1, mh = rx.mh;
    let b: number, fb: number;
    if (mu < mh) {
      const t = Math.sqrt(clamp((mh - mu) / (1 + mh), 0, 1));
      const pm = (1 - t) * (hf - 1);
      let m0 = Math.floor(pm); if (m0 > hf - 2) m0 = hf - 2;
      b = m0; fb = pm - m0;
    } else {
      const t = Math.sqrt(clamp((mu - mh) / (1 - mh), 0, 1));
      const pm = t * (hf - 1);
      let m0 = Math.floor(pm); if (m0 > hf - 2) m0 = hf - 2;
      b = hf + m0; fb = pm - m0;
    }
    const a = mu * mx.mc, bb = Math.sqrt(Math.max(0, (1 - mu * mu) * (1 - mx.mc * mx.mc)));
    const nuc = clamp(nu, a - bb, a + bb);
    const pn = clamp((nuc + 1) * 0.5, 0, 1) * (this.nN - 1);
    let n0 = Math.floor(pn); if (n0 > this.nN - 2) n0 = this.nN - 2;
    const fd = pn - n0;
    const sS = this.nN, sM = this.nS * sS, sR = this.nMu * sM, T = this.ms;
    const base = rx.i * sR + b * sM + mx.c * sS + n0;
    out[0] = 0; out[1] = 0; out[2] = 0;
    for (let ia = 0; ia < 2; ia++) {
      const wa = ia ? rx.f : 1 - rx.f;
      for (let ib = 0; ib < 2; ib++) {
        const wb = ib ? fb : 1 - fb;
        for (let ic = 0; ic < 2; ic++) {
          const wc = ic ? mx.f : 1 - mx.f;
          const w = wa * wb * wc, idx = (base + ia * sR + ib * sM + ic * sS) * 3;
          for (let k = 0; k < 3; k++) out[k]! += w * (T[idx + k]! * (1 - fd) + T[idx + 3 + k]! * fd);
        }
      }
    }
    return out;
  }

  // ── single scattering ───────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
  /** The distance along a view ray from an eye at radius `r` to where it enters the air: 0 for an eye in the air, -1 for a ray from outside that never meets it. */
  entry(r: number, mu: number): number {
    if (r <= this.Rt) return 0;
    const d = r * r * (mu * mu - 1) + this.Rt * this.Rt;
    if (mu >= 0 || d <= 0) return -1;
    return -r * mu - Math.sqrt(d);
  }

  /**
   * The single scattered radiance of one view ray (per unit star irradiance outside; a colour), marched through the air with `medium`'s aerosol over `steps` steps, with the solver's
   * step law (denser at both ends). `mu` is the ray's cosine from the zenith, `nu` its cosine from the star, `mus` the star's cosine at the eye. A ray that meets the ground ends there
   * (what the ground sends up it is not in this: see `ground`). `trOut`, if given, takes the ray's transmittance to where it ends (the ground, or the top of the air), a colour.
   */
  single(h: number, mu: number, mus: number, nu: number, steps: number, medium: Medium = MEAN_MEDIUM, out: number[] | Float64Array = [0, 0, 0] as number[], trOut?: number[] | Float64Array): number[] | Float64Array {
    const Rb = this.Rb, r = Rb + Math.max(0, h), mh = this.muH(r);
    const s0 = this.entry(r, mu);
    const dEnd = mu < mh ? this.distGround(r, mu) : this.distTop(r, mu);
    const aer = medium.aerosol;
    const pr = phaseR(nu), pm = phaseM(nu, this.g);
    let ar = 0, ag = 0, ab = 0, tr = 0, tg = 0, tb = 0;
    const Ts: number[] = [0, 0, 0];
    if (s0 >= 0 && dEnd > s0) {
      const len = dEnd - s0;
      for (let i = 0; i < steps; i++) {
        const x = (i + 0.5) / steps;
        const q = Math.sin(0.5 * Math.PI * x), c = Math.cos(0.5 * Math.PI * x);
        const s = s0 + len * q * q, ds = len * Math.PI * q * c / steps;
        let r2 = r * r + 2 * r * mu * s + s * s; if (r2 < Rb * Rb) r2 = Rb * Rb;
        const ri = Math.sqrt(r2), hi = ri - Rb;
        const musi = clamp((r * mus + s * nu) / ri, -1, 1);
        const dR = Math.exp(-hi / this.hR), dM = aer * Math.exp(-hi / this.hM), dO = rhoO(hi);
        this.trans3(hi, musi, Ts);
        // the three channels: the weight of this step is the light that reaches it (beam), is scattered here, and gets back to the eye (the path so far plus half this step)
        const er = this.kRe[0] * dR + this.kMe[0] * dM + this.kOe[0] * dO, eg = this.kRe[1] * dR + this.kMe[1] * dM + this.kOe[1] * dO, eb = this.kRe[2] * dR + this.kMe[2] * dM + this.kOe[2] * dO;
        const wr = Math.exp(-(tr + 0.5 * er * ds)) * Ts[0]! * ds, wg = Math.exp(-(tg + 0.5 * eg * ds)) * Ts[1]! * ds, wb = Math.exp(-(tb + 0.5 * eb * ds)) * Ts[2]! * ds;
        ar += (this.kRs[0] * dR * pr + this.kMs[0] * dM * pm) * wr;
        ag += (this.kRs[1] * dR * pr + this.kMs[1] * dM * pm) * wg;
        ab += (this.kRs[2] * dR * pr + this.kMs[2] * dM * pm) * wb;
        tr += er * ds; tg += eg * ds; tb += eb * ds;
      }
    }
    out[0] = ar * this.sunToa[0]; out[1] = ag * this.sunToa[1]; out[2] = ab * this.sunToa[2];
    if (trOut) { trOut[0] = Math.exp(-tr); trOut[1] = Math.exp(-tg); trOut[2] = Math.exp(-tb); }
    return out;
  }

  /** The transmittance of a view ray (the eye's cosine `mu` from the zenith) to where it ends, the ground or the top of the air: a colour; `steps` as `single`, which this is the cheaper half of. */
  viewTrans(h: number, mu: number, steps: number, medium: Medium = MEAN_MEDIUM, out: number[] | Float64Array = [0, 0, 0] as number[]): number[] | Float64Array {
    const Rb = this.Rb, r = Rb + Math.max(0, h), mh = this.muH(r);
    const s0 = this.entry(r, mu);
    const dEnd = mu < mh ? this.distGround(r, mu) : this.distTop(r, mu);
    const aer = medium.aerosol;
    let tr = 0, tg = 0, tb = 0;
    if (s0 >= 0 && dEnd > s0) {
      const len = dEnd - s0;
      for (let i = 0; i < steps; i++) {
        const x = (i + 0.5) / steps;
        const q = Math.sin(0.5 * Math.PI * x), c = Math.cos(0.5 * Math.PI * x);
        const s = s0 + len * q * q, ds = len * Math.PI * q * c / steps;
        let r2 = r * r + 2 * r * mu * s + s * s; if (r2 < Rb * Rb) r2 = Rb * Rb;
        const hi = Math.sqrt(r2) - Rb;
        const dR = Math.exp(-hi / this.hR), dM = aer * Math.exp(-hi / this.hM), dO = rhoO(hi);
        tr += (this.kRe[0] * dR + this.kMe[0] * dM + this.kOe[0] * dO) * ds;
        tg += (this.kRe[1] * dR + this.kMe[1] * dM + this.kOe[1] * dO) * ds;
        tb += (this.kRe[2] * dR + this.kMe[2] * dM + this.kOe[2] * dO) * ds;
      }
    }
    out[0] = Math.exp(-tr); out[1] = Math.exp(-tg); out[2] = Math.exp(-tb);
    return out;
  }

  /**
   * The sky's radiance along one view ray: single scattering (marched) plus everything else (the table). Per unit star irradiance outside; a colour. From above the air the ray is
   * followed from where it enters it (the radiance does not change along a ray in empty space), so the table is read at the top of the air.
   */
  radiance(h: number, mu: number, mus: number, nu: number, steps = 24, medium: Medium = MEAN_MEDIUM, out: number[] | Float64Array = [0, 0, 0] as number[]): number[] | Float64Array {
    const s = this.single(h, mu, mus, nu, steps, medium, out);
    const m = this._m;
    const r = this.Rb + Math.max(0, h);
    if (r > this.Rt) {
      const s0 = this.entry(r, mu);
      if (s0 < 0) { out[0] = 0; out[1] = 0; out[2] = 0; return out; }
      this.ms3(this.H, clamp((r * mu + s0) / this.Rt, -1, 1), clamp((r * mus + s0 * nu) / this.Rt, -1, 1), nu, m);
    } else this.ms3(h, mu, mus, nu, m);
    s[0]! += m[0]!; s[1]! += m[1]!; s[2]! += m[2]!;
    return s;
  }
  private _m: number[] = [0, 0, 0];
}
